"use client";

import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "./utils";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

const SheetTrigger = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Trigger>
>((props, ref) => <SheetPrimitive.Trigger data-slot="sheet-trigger" ref={ref} {...props} />);
SheetTrigger.displayName = SheetPrimitive.Trigger.displayName;

const SheetClose = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Close>
>((props, ref) => <SheetPrimitive.Close data-slot="sheet-close" ref={ref} {...props} />);
SheetClose.displayName = SheetPrimitive.Close.displayName;

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-overlay data-[state=closed]:animate-out data-[state=open]:animate-in",
      className,
    )}
    data-slot="sheet-overlay"
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const safeAreaTop = "max(1rem, env(safe-area-inset-top, 0px))";
const safeAreaRight = "max(1rem, env(safe-area-inset-right, 0px))";
const safeAreaBottom = "max(1rem, env(safe-area-inset-bottom, 0px))";
const safeAreaLeft = "max(1rem, env(safe-area-inset-left, 0px))";

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & {
    side?: "top" | "right" | "bottom" | "left";
  }
>(({ side = "right", className, children, style, ...props }, ref) => {
  const contentStyle: React.CSSProperties = {
    ...style,
    "--sheet-safe-top": safeAreaTop,
    "--sheet-safe-right": safeAreaRight,
    "--sheet-safe-bottom": safeAreaBottom,
    "--sheet-safe-left": safeAreaLeft,
  } as React.CSSProperties;

  const closeAtViewportEdge = side === "right" || side === "top";
  const closeStyle: React.CSSProperties = closeAtViewportEdge
    ? { top: "var(--sheet-safe-top)", right: "var(--sheet-safe-right)" }
    : { top: "1rem", right: "1rem" };

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        className={cn(
          "fixed z-50 flex min-h-0 flex-col gap-4 overflow-y-auto border-border bg-background shadow-xl transition ease-in-out data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:duration-300 data-[state=open]:duration-500",
          side === "right" && [
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-[85vw] border-l sm:max-w-sm",
            "pt-[var(--sheet-safe-top)] pr-[var(--sheet-safe-right)] pb-[var(--sheet-safe-bottom)] pl-4 sm:pl-6",
          ],
          side === "left" && [
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-[85vw] border-r sm:max-w-sm",
            "pt-[var(--sheet-safe-top)] pr-4 pb-[var(--sheet-safe-bottom)] pl-[var(--sheet-safe-left)] sm:pr-6",
          ],
          side === "top" && [
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto max-h-[85dvh] border-b",
            "px-4 pt-[var(--sheet-safe-top)] pb-4 sm:px-6 sm:pb-6",
          ],
          side === "bottom" && [
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto max-h-[85dvh] border-t",
            "px-4 pt-4 pb-[var(--sheet-safe-bottom)] sm:px-6 sm:pt-6",
          ],
          className,
        )}
        data-slot="sheet-content"
        ref={ref}
        style={contentStyle}
        {...props}
      >
        {children}
        <SheetPrimitive.Close
          className="absolute z-10 inline-flex items-center justify-center rounded-full border border-border/60 bg-background/88 p-1.5 opacity-80 ring-offset-background transition-[background-color,border-color,opacity] hover:border-brand-accent-1/20 hover:bg-accent hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none max-md:size-[44px] max-md:p-0 [&_svg]:pointer-events-none [&_svg]:shrink-0"
          style={closeStyle}
        >
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("flex flex-col gap-1.5 p-4 pr-12 sm:p-6 sm:pr-14", className)}
      data-slot="sheet-header"
      {...props}
    />
  );
}

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    className={cn("font-medium text-foreground", className)}
    data-slot="sheet-title"
    ref={ref}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    className={cn("text-muted-foreground text-sm", className)}
    data-slot="sheet-description"
    ref={ref}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger };
